// Boss sprite prototypes (v0.8.9 prototype).
//
// Hand-authored ASCII silhouettes. ~6 lines tall, ~10-12 chars
// wide, designed to read at the wall-TV distance. Each sprite
// is a string array; lines are joined with "\n" for a <pre>-
// style block. Stable per-boss assignment via stableHash(name).
//
// Style constraints:
//   - 5-7 lines, 9-13 chars per line (compact, fits a 12rem card)
//   - monochrome: each cell is " ", "█", "▓", "▒", "░", or a
//     punctuation glyph for eyes/tusks
//   - no text inside the sprite (the boss name is the name field)
//   - silhouette must read as a creature at 14px mono — not a logo
//
// Why hand-authored vs generated: ASCII art is small, deterministic,
// and the variations we want (hunched humanoid, tentacle, floating
// eye, swarm, beast) are easier to draw by hand than to coax from a
// procedural generator. If we end up wanting more variety later,
// a JSFuck-style procedural library would slot in here without
// changing the consumer.
//
// Once a design is picked, the chosen sprite(s) move into the
// production public-board component and this file is either
// imported from there or copied in.

export type SpriteId = 'hunched' | 'tentacle' | 'eye' | 'swarm' | 'beast' | 'armored' | 'dragon' | 'golem' | 'tombstone';

export interface SpriteDef {
  id: SpriteId;
  // Lines for the static frame. Each line is the same length
  // (right-padded with spaces).
  lines: string[];
  // Suggested accent color class (tailwind).
  accent: string;
  // Label for the prototype picker.
  label: string;
}

export const SPRITES: SpriteDef[] = [
  {
    id: 'hunched',
    label: 'Hunched humanoid',
    accent: 'text-red-300',
    lines: [
      '   ▄▄██▄▄   ',
      '  █     █  ',
      ' █  ◉ ◉  █ ',
      ' █   ▽   █ ',
      '  █▄█▄█▄█  ',
      '  █ █ █ █  ',
      '   ▀   ▀   ',
    ],
  },
  {
    id: 'tentacle',
    label: 'Tentacled thing',
    accent: 'text-purple-300',
    lines: [
      '  ▄▀▀▀▀▄   ',
      ' █ ◉ ◉ █   ',
      ' █  ▽  █▄  ',
      '  ████▀ ▀▄ ',
      ' ▀█▀ ▀█▀   ',
      '▀▀  ▀▀ ▀   ',
      '           ',
    ],
  },
  {
    id: 'eye',
    label: 'Floating eye',
    accent: 'text-amber-300',
    lines: [
      '   ▄▀▀▀▄   ',
      ' ▄█     █▄ ',
      '█  ◉   ◉  █',
      '█    ▽    █',
      '█  ◉   ◉  █',
      ' █     █  ',
      '  ▀▄▄▄▀   ',
    ],
  },
  {
    id: 'swarm',
    label: 'Swarm',
    accent: 'text-emerald-300',
    lines: [
      '  ▄  ▄▄  ▄  ',
      ' ██▄ ██ ▄██ ',
      '▄█ ▀▄██▄▀ █▄',
      '██ ◉▄██▄◉ ██',
      '▀█ ▀▄██▄▀ █▀',
      ' ██▀ ██ ▀██ ',
      '  ▀  ▀▀  ▀  ',
    ],
  },
  {
    id: 'beast',
    label: 'Beast',
    accent: 'text-amber-500',
    lines: [
      '  ▄▄▄▄▄▄   ',
      ' █ ◉ ◉ █▄  ',
      '█  ▽▽▽  █▄ ',
      '▄█▄   ▄█▄ █ ',
      '█  ▄▄▄▄  █ ',
      '██ ████ ██ ',
      ' ▀ ▀   ▀ ▀ ',
    ],
  },
  {
    id: 'armored',
    label: 'Armored',
    accent: 'text-zinc-300',
    lines: [
      '  ▄█▀█▀█▄  ',
      ' █  ◉ ◉  █ ',
      ' █   ▽   █ ',
      ' █▄▄▄▄▄▄▄█ ',
      ' █▄▄█▄▄█▄█ ',
      ' █▄▄▄▄▄▄▄█ ',
      '   ▄   ▄   ',
    ],
  },
  {
    id: 'dragon',
    label: 'Dragon',
    accent: 'text-orange-400',
    lines: [
      '      ▄▄▄▄▄ ',
      '  ▄█▀     █',
      '█▀  ▄ ◉◉ ▄ ',
      '█   ▀    ▀ ',
      '█▄  ▀▄▄▄▀  ',
      ' ▄▄██▄▄▄▄ ',
      '▀▀ ▀▀   ▀▀',
    ],
  },
  {
    id: 'golem',
    label: 'Golem',
    accent: 'text-stone-400',
    lines: [
      '  ▄▄██▄▄   ',
      ' █  ◉ ◉  █ ',
      ' █   ▽   █ ',
      ' █ ▄▄▄▄▄ █ ',
      '██ ████ ██ ',
      '██ ▀▄▄▀ ██ ',
      ' ▀▄ ▄ ▄ ▀ ',
    ],
  },
];

// v0.8.9: tombstone sprite used to overlay a defeated boss.
// Returns the same envelope as the active sprites so BossCard
// can swap it in without a code branch. Adventure-flavored:
// 'R.I.P.' replaced with 'FALLEN' + the rarity label so the
// card still tells you what was beaten.
export const TOMBSTONE_SPRITE: SpriteDef = {
  id: 'tombstone',
  label: 'Tombstone (vanquished)',
  accent: 'text-zinc-400',
  lines: [
    '     ▄▄▄     ',
    '    █FALLEN█ ',
    '    █  ▋  █ ',
    '    █     █ ',
    '   █▀▀▀▀▀▀▀█ ',
    '  █ ▀▀ ▀▀ █  ',
    ' █  ▀▀▀ ▀▀ █ ',
  ],
};

// Stable hash so a boss name always picks the same sprite. Not
// cryptographic — just a quick mix of characters. Same input
// always returns the same index, even across deploys.
export function stableHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function spriteFor(bossName: string): SpriteDef {
  const idx = stableHash(bossName) % SPRITES.length;
  return SPRITES[idx]!;
}
